"use client";

import { useClarityAnalytics, useUmamiAnalytics } from "@analytics";
import { LocaleLink } from "@i18n/routing";
import { PAID_PLAN_TIERS, config } from "@repo/config";
import { useLoginModal } from "@saas/auth/components/LoginModalProvider";
import { useSession } from "@saas/auth/hooks/use-session";
import { useCredits } from "@saas/credits/hooks/use-credits";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { NavBadge } from "@saas/shared/components/NavBadge";
import { UserMenu } from "@saas/shared/components/UserMenu";
import type { NavItem } from "@saas/shared/config/nav-items";
import { reloadIfSamePage, useNavItems } from "@saas/shared/config/nav-items";
import { LocaleSwitch } from "@shared/components/LocaleSwitch";
import { Logo } from "@shared/components/Logo";
import { useFullscreenMenu } from "@shared/hooks/use-fullscreen-menu";
import { Button } from "@ui/components/button";
import { CrownIcon, MenuIcon, XIcon, Zap } from "lucide-react";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { Fragment, useEffect, useRef, useState } from "react";

const MOBILE_MENU_ID = "app-mobile-menu";

export function MobileHeader() {
	const t = useTranslations("saas.sidebar");
	const commonT = useTranslations("common");
	const [isOpen, setIsOpen] = useState(false);
	const { user } = useSession();
	const { showLoginModal } = useLoginModal();
	const { getRemainingCredits, hasUnlimitedCredits } = useCredits();
	const { hasSubscription } = usePurchases();
	const isPaidPlan = hasSubscription([...PAID_PLAN_TIERS]);
	const { trackEvent: trackUmamiEvent } = useUmamiAnalytics();
	const { trackEvent: trackClarityEvent } = useClarityAnalytics();

	const { main, creation, models } = useNavItems();
	// 全屏菜单铺在页头下面，打开那一刻量页头底边（页头是 sticky，可能带语言条）
	const headerRef = useRef<HTMLDivElement>(null);
	const [menuTop, setMenuTop] = useState(0);
	const closeMenu = () => setIsOpen(false);
	// 和页头的 md:hidden 同一个断点
	useFullscreenMenu(isOpen, closeMenu, "(min-width: 48rem)");
	// (saas)/layout 共用这一个实例，站内跳转不会卸载它。头像抽屉的链接只关抽屉、
	// 不经过 closeMenu，所以按路由兜底：路径一变就收起菜单
	const pathname = usePathname();
	useEffect(() => {
		setIsOpen(false);
	}, [pathname]);

	const NavRow = ({ item }: { item: NavItem }) => (
		<LocaleLink
			href={item.href}
			onClick={closeMenu}
			className={`flex min-h-11 items-center gap-3 px-3 rounded-md text-base font-medium transition-colors ${
				item.active
					? "bg-primary/10 text-primary"
					: "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
			}`}
		>
			<item.Icon className="h-4 w-4 shrink-0" />
			<div className="flex items-center gap-2 flex-1 min-w-0">
				<span className="truncate">{item.title}</span>
				{item.badge ? <NavBadge label={item.badge} /> : null}
			</div>
		</LocaleLink>
	);

	// 和营销页 NavBar 同一套手机交互：logo 在左，头像 + 汉堡在右，汉堡开全屏菜单、
	// × 留在原位；头像点开是底部抽屉（UserMenu mobileSheet）
	return (
		<>
			<div
				ref={headerRef}
				// 固定 56（照 DeeVid）：原来 p-4 + 44 的按钮撑到 77，比内容还厚（10/21）
				className="md:hidden flex h-14 items-center justify-between px-4 border-b bg-background sticky z-50"
				style={{
					top: "var(--language-banner-height, 0px)",
				}}
			>
				<LocaleLink href="/" onClick={closeMenu}>
					<Logo withLabel={true} size="sm" />
				</LocaleLink>
				<div className="flex items-center gap-3">
					{/* 和营销页 NavBar 同位置：头像 / 登录钮在汉堡左边 */}
					{config.i18n.enabled && <LocaleSwitch />}
					{user && <UserMenu mobileSheet />}
					{!user && (
						<Button
							variant="ghost"
							className="h-11 px-2 font-semibold text-sm hover:bg-transparent"
							onClick={() => {
								closeMenu();
								showLoginModal(
									window.location.pathname +
										window.location.search,
								);
							}}
						>
							{commonT("menu.login")}
						</Button>
					)}
					<Button
						variant="ghost"
						size="icon"
						className="-mr-2"
						aria-expanded={isOpen}
						aria-controls={MOBILE_MENU_ID}
						onClick={() => {
							setMenuTop(
								headerRef.current?.getBoundingClientRect()
									.bottom ?? 0,
							);
							setIsOpen((open) => !open);
						}}
					>
						{isOpen ? (
							<XIcon className="h-5 w-5" />
						) : (
							<MenuIcon className="h-5 w-5" />
						)}
						<span className="sr-only">{t("toggleSidebar")}</span>
					</Button>
				</div>
			</div>

			{isOpen && (
				<div
					id={MOBILE_MENU_ID}
					className="md:hidden fixed inset-x-0 bottom-0 z-40 overflow-y-auto bg-background"
					style={{ top: menuTop }}
				>
					<div className="flex flex-col gap-6 px-3 pt-4 pb-10">
						{user && (
							<div className="px-1">
								<LocaleLink
									href="/subscription"
									onClick={() => {
										// Paid users land here to check their
										// balance, not to upgrade — only free
										// users clicking this are upgrade intent.
										if (!isPaidPlan) {
											void trackUmamiEvent(
												"upgrade-clicked",
												{
													location:
														"mobile-header-credits-badge",
												},
											);
											trackClarityEvent(
												"upgrade-clicked",
											);
										}
										closeMenu();
									}}
									className="flex min-h-11 items-center gap-2 group transition-all duration-300 rounded-lg px-3 border border-transparent bg-accent/20 hover:bg-accent/40"
								>
									<div className="relative shrink-0 flex items-center justify-center">
										<Zap className="h-3.5 w-3.5 text-primary/60 fill-primary/10 group-hover:scale-110 transition-transform duration-300" />
										<div className="absolute inset-0 bg-primary/20 blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
									</div>

									<div className="flex items-center justify-between flex-1 gap-2 overflow-hidden">
										<span className="text-[10px] font-bold text-muted-foreground/50 uppercase tracking-[0.15em] whitespace-nowrap">
											{t("energy")}
										</span>
										<span className="text-sm font-bold text-foreground/60 tabular-nums">
											{hasUnlimitedCredits(
												"IMAGE_GENERATION",
											)
												? "∞"
												: getRemainingCredits(
														"IMAGE_GENERATION",
													)}
										</span>
									</div>
								</LocaleLink>
							</div>
						)}

						<div className="space-y-1">
							{main.map((item) => (
								<NavRow key={item.href} item={item} />
							))}
						</div>

						<nav aria-label={t("creation")} className="space-y-1">
							<div className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
								{t("creation")}
							</div>
							{creation.map((item) => (
								<Fragment key={item.href}>
									<NavRow item={item} />
									{/* 手机没有悬停，子项直接缩进列在父项下面 */}
									{item.children?.map((child) => (
										<LocaleLink
											key={child.href}
											href={child.href}
											onClick={(event) => {
												closeMenu();
												reloadIfSamePage(event);
											}}
											aria-current={
												child.active
													? "page"
													: undefined
											}
											className={`flex min-h-10 items-center gap-3 rounded-md pr-3 pl-10 text-sm transition-colors ${
												child.active
													? "bg-primary/10 text-primary font-medium"
													: "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
											}`}
										>
											<child.Icon className="h-4 w-4 shrink-0" />
											<span className="truncate">
												{child.title}
											</span>
											{child.badge ? (
												<NavBadge label={child.badge} />
											) : null}
										</LocaleLink>
									))}
								</Fragment>
							))}
						</nav>

						<nav aria-label={t("models")} className="space-y-1">
							<div className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
								{t("models")}
							</div>
							{models.map((item) => (
								<NavRow key={item.href} item={item} />
							))}
						</nav>

						<div className="space-y-4 border-t px-1 pt-4">
							<LocaleLink
								href="/subscription"
								onClick={() => {
									void trackUmamiEvent("upgrade-clicked", {
										location: "mobile-header",
									});
									trackClarityEvent("upgrade-clicked");
									closeMenu();
								}}
								className="flex items-center gap-3 px-3 py-3 rounded-lg bg-gradient-to-br from-amber-100 via-amber-200 to-amber-100 dark:from-amber-900/40 dark:via-amber-800/40 dark:to-amber-900/40 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100"
							>
								<div className="bg-white/50 dark:bg-amber-900/40 p-1.5 rounded-md shrink-0 ring-1 ring-amber-300/30">
									<CrownIcon className="h-4 w-4 shrink-0 fill-amber-500" />
								</div>
								<div className="flex-1 overflow-hidden">
									<div className="text-[13px] font-bold truncate leading-snug">
										{t("upgrade")}
									</div>
									<div className="text-[11px] font-medium opacity-80 truncate leading-tight">
										{t("getProFeatures")}
									</div>
								</div>
							</LocaleLink>

							{/* 登录后头像已在页头；没登录时这里是「Start for free」入口 */}
							{!user && <UserMenu showUserName />}
						</div>
					</div>
				</div>
			)}
		</>
	);
}
